import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Plus, MapPin, Pencil, Trash2, ArrowLeft } from "lucide-react";
import { getMyELQRs, deleteELQR } from "../../services/createEventService";
import { normalizeSavedLocation } from "../../utils/eventLocation";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Badge from "../../components/common/Badge";
import EmptyState from "../../components/common/EmptyState";
import ConfirmModal from "../../components/common/ConfirmModal";
import AddLocationModal from "../../components/create-event/AddLocationModal";
import { color } from "../../styles/theme";

/**
 * Locations — standalone management page for saved locations (ELQR),
 * ported from mobile's locations screen (src/Screens/locations). Reuses the
 * same full CRUD (getMyELQRs/deleteELQR + AddLocationModal) already built
 * for Create Event's location picker — this page just exposes it outside
 * the picker-modal context.
 */
const Locations = () => {
  const { t } = useTranslation(["locations", "common"]);
  const navigate = useNavigate();
  // Present when opened via Event Settings' "Locations" row, so Back can
  // return there instead of leaving the user stranded on this standalone page.
  const fromEventId = useLocation().state?.fromEventId;
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    getMyELQRs(1, 50)
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.Result || [];
        setLocations(raw.map(normalizeSavedLocation));
      })
      .catch(() => toast.error(t("toast.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteELQR(deleteTarget.Id, deleteTarget.Radius);
      toast.success(t("toast.deleteSuccess"));
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("toast.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      {fromEventId && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={s.backLink}
          onClick={() => navigate(`/events/${fromEventId}`, { state: { initialTab: "Settings" } })}
        >
          <ArrowLeft size={13} strokeWidth={2.25} /> {t("common:actions.backToEventSettings")}
        </button>
      )}
      <div style={s.headerRow}>
        <h2 style={s.title}>{t("title")}</h2>
        <Button icon={Plus} onClick={() => { setEditingLocation(null); setShowAddLocation(true); }}>{t("addLocation")}</Button>
      </div>

      {locations.length === 0 ? (
        <EmptyState icon={MapPin} text={t("empty")} />
      ) : (
        <div style={s.list}>
          {locations.map((loc) => (
            <Card key={loc.Id} style={s.card}>
              <div style={s.cardBody}>
                <div style={s.name}>{loc.LocationName || t("unnamedLocation")}</div>
                <div style={s.address}>{loc.Address}</div>
                <div style={s.meta}>
                  <span>{t("radius", { value: loc.Radius })}</span>
                  {loc.IsGeofenceEnabled && <Badge variant="primary">{t("zone")}</Badge>}
                  {loc.LocationCheckInPoints.length > 0 && <Badge variant="primary">{t("checkInPins", { count: loc.LocationCheckInPoints.length })}</Badge>}
                </div>
              </div>
              <div style={s.cardActions}>
                <button type="button" style={s.iconBtn} onClick={() => { setEditingLocation(loc); setShowAddLocation(true); }} title={t("common:actions.edit")}><Pencil size={13} strokeWidth={2.25} /></button>
                <button type="button" style={{ ...s.iconBtn, ...s.deleteIconBtn }} onClick={() => setDeleteTarget(loc)} title={t("common:actions.delete")}><Trash2 size={13} strokeWidth={2.25} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showAddLocation && (
        <AddLocationModal
          locationObj={editingLocation}
          defaultName={t("defaultLocationName", { number: locations.length + 1 })}
          onClose={() => { setShowAddLocation(false); setEditingLocation(null); }}
          onSaved={load}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t("deleteModal.title")}
          message={t("deleteModal.message", { name: deleteTarget.LocationName || t("deleteModal.thisLocation") })}
          confirmText={t("common:actions.delete")}
          danger
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => !deleting && setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

const s = {
  backLink: { display: "inline-flex", marginBottom: 12 },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 },
  title: { fontSize: 20, fontWeight: 800, color: color.textPrimary, margin: 0, letterSpacing: -0.3 },
  list: { display: "flex", flexDirection: "column", gap: 10, maxWidth: 560 },
  card: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px" },
  cardBody: { flex: 1, minWidth: 0 },
  name: { fontWeight: 700, fontSize: 14, color: color.textPrimary, marginBottom: 4 },
  address: { fontSize: 12.5, color: color.textSecondary, marginBottom: 8 },
  meta: { display: "flex", gap: 8, alignItems: "center", fontSize: 11.5, color: color.textMuted },
  cardActions: { display: "flex", gap: 8, flexShrink: 0, marginLeft: 12 },
  iconBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    border: `1px solid ${color.borderStrong}`, background: color.surface, color: color.gray600,
    borderRadius: 8, width: 30, height: 30, cursor: "pointer",
  },
  deleteIconBtn: { borderColor: color.danger50, background: color.danger50, color: color.danger500 },
};

export default Locations;
